import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useSelector } from 'react-redux';
import api from '../../redux/features/auth/axiosAuthHelper';
import { type RootState } from '../../redux/store';
import { apiError, type ApiError } from '../../services/apiError';
import { workflowLabels } from './accountApi';

interface Entry {
  id: number;
  actor_email: string | null;
  timestamp: string;
  action: string;
  changes: Record<string, unknown[]>;
  additional_data?: { import_id?: string };
}
const labels: Record<string, string> = {
  account_number: 'Account number',
  account_name: 'Account name',
  is_archived: 'Archived',
  ...workflowLabels,
};
function validEntry(value: Entry): boolean {
  return (
    !!value &&
    Number.isSafeInteger(value.id) &&
    typeof value.action === 'string' &&
    (value.actor_email === null || typeof value.actor_email === 'string') &&
    Number.isFinite(Date.parse(value.timestamp)) &&
    !!value.changes &&
    typeof value.changes === 'object' &&
    !Array.isArray(value.changes) &&
    Object.values(value.changes).every((change) => Array.isArray(change) && change.length === 2)
  );
}
function display(value: unknown): string {
  if (value === null || value === 'None') return '—';
  if (value === true || value === 'True') return 'Yes';
  if (value === false || value === 'False') return 'No';
  return typeof value === 'string' ? value : (JSON.stringify(value) ?? '—');
}

export default function AccountHistory({ accountId }: { accountId: number }) {
  const access = useSelector((state: RootState) => state.auth.access);
  const [open, setOpen] = useState(false);
  const [offset, setOffset] = useState(0);
  const [reload, setReload] = useState(0);
  const [entries, setEntries] = useState<Entry[]>([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    setEntries([]);
    api
      .get(`customer-accounts/${accountId}/history/?limit=20&offset=${offset}`, {
        headers: { Authorization: `Bearer ${access}` },
        signal: controller.signal,
        timeout: 30000,
      })
      .then(({ data }) => {
        if (controller.signal.aborted) return;
        if (
          !Number.isSafeInteger(data?.count) ||
          data.count < 0 ||
          !Array.isArray(data?.results) ||
          data.results.length > 20 ||
          !data.results.every(validEntry)
        )
          throw new Error('Invalid history response');
        setEntries(data.results);
        setCount(data.count);
      })
      .catch((failure) => {
        if (!controller.signal.aborted) setError(apiError(failure, 'Account history'));
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [open, accountId, access, offset, reload]);
  return (
    <Box sx={{ flex: 1, minWidth: 0, color: 'text.primary' }}>
      <Button onClick={() => setOpen((value) => !value)} aria-expanded={open}>
        {open ? 'Hide history' : 'View history'}
      </Button>
      {open && (
        <>
          {loading && <CircularProgress size={24} aria-label="Loading account history" />}
          {error && (
            <Alert
              severity="error"
              action={
                error.status !== 401 &&
                error.status !== 403 && (
                  <Button color="inherit" onClick={() => setReload((value) => value + 1)}>
                    Retry
                  </Button>
                )
              }
            >
              {error.message}
            </Alert>
          )}
          {!loading && !error && (
            <>
              <Typography color="text.primary" variant="caption">
                Times are shown in your local time zone.
              </Typography>
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small" aria-label="Account history">
                  <TableHead>
                    <TableRow>
                      <TableCell>When / who</TableCell>
                      <TableCell>Change</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {entries.map((entry) => (
                      <TableRow key={entry.id}>
                        <TableCell sx={{ verticalAlign: 'top' }}>
                          {new Date(entry.timestamp).toLocaleString()}
                          <br />
                          {entry.actor_email ?? 'System'}
                          <br />
                          {entry.action}
                          {typeof entry.additional_data?.import_id === "string" && /^[a-f0-9-]{36}$/i.test(entry.additional_data.import_id) && <><br /><Link to={`/customer-accounts/imports/${entry.additional_data.import_id}`}>View import result</Link></>}
                        </TableCell>
                        <TableCell>
                          {Object.entries(entry.changes).map(([key, values]) => (
                            <Typography key={key} color="text.primary" variant="body2">
                              {labels[key] ?? key}: {display(values[0])} → {display(values[1])}
                            </Typography>
                          ))}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
              {!entries.length && (
                <Typography color="text.primary" variant="body2">
                  No history found.
                </Typography>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Button disabled={offset === 0} onClick={() => setOffset((value) => Math.max(0, value - 20))}>
                  Previous changes
                </Button>
                <Typography color="text.primary" variant="caption">
                  {count ? `${offset + 1}–${offset + entries.length} of ${count} changes` : '0 changes'}
                </Typography>
                <Button disabled={offset + 20 >= count} onClick={() => setOffset((value) => value + 20)}>
                  Next changes
                </Button>
              </Box>
            </>
          )}
        </>
      )}
    </Box>
  );
}
